import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Upload, ChevronLeft, ChevronRight, Package } from 'lucide-react';
import { useProducts } from '../hooks/useProducts.js';
import { formatCurrency, formatDate } from '../lib/format.js';
import ProductsUploadModal from '../components/ProductsUploadModal.jsx';

const STATUS_BADGE = {
  Live:    { label: 'Live',    className: 'bg-green-100 text-green-700' },
  Pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
};

function StatusBadge({ status }) {
  const conf = STATUS_BADGE[status] ?? { label: status ?? '—', className: 'bg-slate-100 text-slate-500' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${conf.className}`}>
      {conf.label}
    </span>
  );
}

export default function ProductsPage() {
  const navigate = useNavigate();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchDebounced(searchInput.trim().length >= 2 ? searchInput.trim() : '');
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading, error } = useProducts({
    q: searchDebounced || undefined,
    status: statusFilter || undefined,
    page,
    per_page: 50,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const lastImportAt = data?.last_imported_at ?? null;
  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <>
      <div className="space-y-4">
        {/* Header import */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-sm font-medium text-slate-800">Inventaire produits</p>
              {lastImportAt ? (
                <p className="text-xs text-slate-500 mt-0.5">
                  Dernier import : {formatDate(lastImportAt)}
                </p>
              ) : (
                <p className="text-xs text-slate-400 mt-0.5">Aucun import effectué</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium bg-slate-900 text-white rounded-lg hover:bg-slate-700 transition-colors shrink-0"
            >
              <Upload size={14} aria-hidden /> Importer un inventaire (.xlsx)
            </button>
          </div>

          {/* Filtres */}
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1 min-w-0">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Rechercher SKU, référence, nom…"
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="text-sm border border-slate-300 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
              aria-label="Filtre statut produit"
            >
              <option value="">Tous les statuts</option>
              <option value="Live">Live</option>
              <option value="Pending">Pending</option>
            </select>
          </div>

          {total > 0 && (
            <p className="text-xs text-slate-500">
              {total.toLocaleString('fr-FR')} produit{total > 1 ? 's' : ''}
            </p>
          )}
        </div>

        {/* Erreur */}
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            Impossible de charger les produits : {error.message}
          </div>
        )}

        {/* Loading */}
        {isLoading && (
          <div className="space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-16 bg-slate-100 animate-pulse rounded-xl" />
            ))}
          </div>
        )}

        {/* Empty */}
        {!isLoading && !error && rows.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <Package size={32} className="text-slate-300" />
            <p className="text-sm text-slate-500">Aucun produit dans l'inventaire.</p>
            <p className="text-xs text-slate-400">
              Importe un fichier .xlsx pour alimenter le catalogue.
            </p>
          </div>
        )}

        {/* Mobile cards */}
        {!isLoading && rows.length > 0 && (
          <>
            <div className="space-y-2 md:hidden">
              {rows.map((product) => (
                <button
                  key={product.sku}
                  type="button"
                  onClick={() => navigate(`/ventes/produits/${encodeURIComponent(product.sku)}`)}
                  className="w-full text-left bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-3 hover:bg-slate-50 active:bg-slate-100 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-mono text-slate-500 truncate">{product.sku}</p>
                      {product.identification_id && (
                        <p className="text-[11px] font-mono text-slate-400 truncate">ID {product.identification_id}</p>
                      )}
                      <p className="text-sm font-medium text-slate-900 truncate mt-0.5">{product.name ?? '—'}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <StatusBadge status={product.status} />
                      {product.available_quantity != null && (
                        <span className="text-xs text-slate-500">Dispo : {product.available_quantity}</span>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">SKU</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">ID unique</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Réf. OEM</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Nom</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Statut</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Dispo</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Prix</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((product) => (
                    <tr
                      key={product.sku}
                      onClick={() => navigate(`/ventes/produits/${encodeURIComponent(product.sku)}`)}
                      className="hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 font-mono text-xs text-slate-600 whitespace-nowrap">{product.sku}</td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-500 whitespace-nowrap">{product.identification_id ?? '—'}</td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{product.reference ?? '—'}</td>
                      <td className="px-4 py-3 text-slate-800 max-w-[220px] truncate">{product.name ?? '—'}</td>
                      <td className="px-4 py-3 text-slate-500">{product.product_type ?? '—'}</td>
                      <td className="px-4 py-3"><StatusBadge status={product.status} /></td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-700">{product.available_quantity ?? '—'}</td>
                      <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">
                        {product.price_cents != null ? formatCurrency(product.price_cents) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between gap-4">
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={14} aria-hidden /> Précédent
                </button>
                <span className="text-xs text-slate-500">Page {page} / {totalPages}</span>
                <button
                  type="button"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Suivant <ChevronRight size={14} aria-hidden />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {showUploadModal && (
        <ProductsUploadModal onClose={() => setShowUploadModal(false)} />
      )}
    </>
  );
}
